import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';

import EmptyContent from '../../common/EmptyState/Content';
import { CommonTabs } from '../../common/CommonTabs';
import { ReportsList } from './ReportsList';
import { useLazyLoad } from '../../../hocs/useLazyLoad';
import Loader from '../../common/Loader';
import { enhance } from './enhance';
import { IDS } from '../../../constants/ids';

const TAB_KEYS = {
  UNRESOLVED: 'unresolved',
  RESOLVED: 'resolved',
};

const ReportsImpl = ({
  unresolvedCounter,
  resolvedCounter,
  unresolvedList,
  resolvedList,
  fetchResolvedList,
  fetchUnresolvedList,
  loadMoreResolved,
  loadMoreUnresolved,
  loading,
  intl: { formatMessage },
}) => {
  const [activeTab, setActiveTab] = useState(TAB_KEYS.UNRESOLVED);
  const getList = useReportListByTab(unresolvedList, resolvedList);
  const tabs = useTabs(unresolvedCounter, resolvedCounter);
  const rootRef = useRef();
  const loadMore = useCallback(() => {
    if (activeTab === TAB_KEYS.UNRESOLVED) loadMoreUnresolved();
    if (activeTab === TAB_KEYS.RESOLVED) loadMoreResolved();
  }, [activeTab]);

  useLazyLoad({ node: rootRef, loadMore, path: ['parentNode'] });
  const isEmpty = !unresolvedCounter && !resolvedCounter;

  useEffect(() => {
    fetchResolvedList({
      status: 'resolved',
      limit: 10,
      offset: 0,
    });
    fetchUnresolvedList({
      status: 'unresolved',
      limit: 10,
      offset: 0,
    });
  }, []);

  useEffect(() => {
    if (!unresolvedCounter && resolvedCounter) {
      setActiveTab(TAB_KEYS.RESOLVED);
    }
  }, [unresolvedCounter, resolvedCounter]);

  return (
    <div styleName="wrapper" ref={rootRef}>
      {loading && <Loader />}

      {!loading && !isEmpty && (
        <CommonTabs
          onTabSelect={setActiveTab}
          activeTab={activeTab}
          tabList={tabs}
          renderContent={tab => <ReportsList list={getList(tab)} />}
        />
      )}

      {!loading && isEmpty && (
        <div styleName="empty-state">
          <EmptyContent
            text={formatMessage({ id: 'reports.emptyMessage' })}
            icon="icon-megaphone"
          />
        </div>
      )}
    </div>
  );
};

export const Reports = enhance(ReportsImpl);

ReportsImpl.propTypes = {
  unresolvedList: PropTypes.array,
  resolvedList: PropTypes.array,
  unresolvedCounter: PropTypes.number,
  resolvedCounter: PropTypes.number,
  fetchUnresolvedList: PropTypes.func.isRequired,
  fetchResolvedList: PropTypes.func.isRequired,
  loadMoreUnresolved: PropTypes.func.isRequired,
  loadMoreResolved: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  intl: PropTypes.object.isRequired,
};

ReportsImpl.defaultProps = {
  unresolvedList: [],
  resolvedList: [],
  unresolvedCounter: 0,
  resolvedCounter: 0,
};

function useTabs(unresolvedCounter, resolvedCounter) {
  return useMemo(() => {
    const tabs = [];
    if (unresolvedCounter)
      tabs.push({
        label: 'community.reports.unresolved',
        id: IDS.REPORTS_TABS.UNRESOLVED,
        tab: TAB_KEYS.UNRESOLVED,
        counter: unresolvedCounter,
      });

    if (resolvedCounter)
      tabs.push({
        label: 'community.reports.resolved',
        id: IDS.REPORTS_TABS.RESOLVED,
        tab: TAB_KEYS.RESOLVED,
        counter: resolvedCounter,
      });
    return tabs;
  }, [unresolvedCounter, resolvedCounter]);
}

function useReportListByTab(unresolvedList, resolvedList) {
  return useCallback(
    tab => {
      switch (tab) {
        case TAB_KEYS.RESOLVED:
          return resolvedList;
        case TAB_KEYS.UNRESOLVED:
          return unresolvedList;
        default:
          return [];
      }
    },
    [unresolvedList, resolvedList],
  );
}



// WEBPACK FOOTER //
// ./src/components/Communities/Reports/index.jsx